// Shared GLSL snippets (GLSL ES 3.0 via THREE.GLSL3).

export const commonGLSL = /* glsl */ `
#ifndef PI
#define PI 3.141592653589793
#endif
#define INV_PI 0.3183098861837907

float saturate1(float x) { return clamp(x, 0.0, 1.0); }
vec3 saturate3(vec3 x) { return clamp(x, 0.0, 1.0); }
float lum3(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 hash22(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
float hash13(vec3 p3) {
  p3 = fract(p3 * 0.1031);
  p3 += dot(p3, p3.zyx + 31.32);
  return fract((p3.x + p3.y) * p3.z);
}

// Value noise with smooth interpolation, returns [0,1]
float vnoise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash12(i);
  float b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0));
  float d = hash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float vnoise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);
  float n000 = hash13(i);
  float n100 = hash13(i + vec3(1, 0, 0));
  float n010 = hash13(i + vec3(0, 1, 0));
  float n110 = hash13(i + vec3(1, 1, 0));
  float n001 = hash13(i + vec3(0, 0, 1));
  float n101 = hash13(i + vec3(1, 0, 1));
  float n011 = hash13(i + vec3(0, 1, 1));
  float n111 = hash13(i + vec3(1, 1, 1));
  return mix(mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y),
             mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y), u.z);
}
float fbm2(vec2 p, int oct) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 8; i++) {
    if (i >= oct) break;
    s += a * vnoise2(p);
    p = mat2(1.6, 1.2, -1.2, 1.6) * p + 17.13;
    a *= 0.5;
  }
  return s;
}
float fbm3(vec3 p, int oct) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 6; i++) {
    if (i >= oct) break;
    s += a * vnoise3(p);
    p = p * 2.03 + vec3(11.7, 3.1, 7.9);
    a *= 0.5;
  }
  return s;
}

`;

// Earth curvature drop relative to the viewer (meters).
export const curvatureGLSL = /* glsl */ `
uniform vec3 uCurvOrigin;
float curvatureDrop(vec3 wp) {
  vec2 d = wp.xz - uCurvOrigin.xz;
  return dot(d, d) / (2.0 * 6371000.0);
}
`;

// Physically based lighting shared by every lit material.
// Sun/sky values come from GPU textures produced by the atmosphere system:
//   uSkyIrr: 16x1 RGBA16F. texels 0..8 = SH9 sky irradiance coefficients (cosine-convolved),
//            texel 9 = sun radiance scale at the camera (rgb), 10 = ground-bounce irradiance,
//            11 = moon/night ambient, 12 = average sky luminance, 13 = misc (x: cloud cover)
export const lightingGLSL = /* glsl */ `
uniform sampler2D uSkyIrr;
uniform sampler2D uTransLUT;
uniform vec3 uSunDir;        // direction toward the dominant light (sun or moon)
uniform vec3 uSunColorTOA;   // top-of-atmosphere irradiance of dominant light (sun: ~PI, moon: tiny)
uniform float uGroundRadiusMM;
uniform float uAtmoRadiusMM;
uniform sampler2D uCloudShadow;  // cloud transmittance projected along the sun, around camera
uniform vec4 uCloudShadowXform;  // x0, z0, 1/size, strength
uniform float uWetness;
uniform float uSnowCover;
uniform float uLightning;

vec3 skyIrrTexel(int i) { return texelFetch(uSkyIrr, ivec2(i, 0), 0).rgb; }

vec3 shIrradiance(vec3 n) {
  // SH9, coefficients already convolved with the clamped cosine lobe and divided by PI
  vec3 c0 = skyIrrTexel(0), c1 = skyIrrTexel(1), c2 = skyIrrTexel(2), c3 = skyIrrTexel(3);
  vec3 c4 = skyIrrTexel(4), c5 = skyIrrTexel(5), c6 = skyIrrTexel(6), c7 = skyIrrTexel(7), c8 = skyIrrTexel(8);
  float x = n.x, y = n.y, z = n.z;
  vec3 r = c0 * 0.282095
    + c1 * 0.488603 * y + c2 * 0.488603 * z + c3 * 0.488603 * x
    + c4 * 1.092548 * x * y + c5 * 1.092548 * y * z + c6 * 0.315392 * (3.0 * z * z - 1.0)
    + c7 * 1.092548 * x * z + c8 * 0.546274 * (x * x - y * y);
  return max(r, vec3(0.0));
}

vec3 transmittanceAt(float altitudeM, vec3 dir) {
  float r = uGroundRadiusMM + max(altitudeM, 0.0) * 1e-6;
  float mu = dir.y;
  // Earth occlusion of the sun disk near the horizon (soft)
  float horizonMu = -sqrt(max(0.0, 1.0 - (uGroundRadiusMM * uGroundRadiusMM) / (r * r)));
  vec2 uv = vec2(clamp(0.5 + 0.5 * mu, 0.0, 1.0), clamp((r - uGroundRadiusMM) / (uAtmoRadiusMM - uGroundRadiusMM), 0.0, 1.0));
  vec3 t = texture(uTransLUT, uv).rgb;
  return t * smoothstep(horizonMu - 0.004, horizonMu + 0.004, mu);
}

uniform float uCloudFar; // average sun transmittance used beyond the cloud-shadow map
float cloudShadowAt(vec3 wp) {
  vec2 uv = (wp.xz - uCloudShadowXform.xy) * uCloudShadowXform.z;
  float s = texture(uCloudShadow, uv).r;
  vec2 e = abs(uv - 0.5);
  float outside = smoothstep(0.42, 0.5, max(e.x, e.y));
  s = mix(s, uCloudFar, outside);
  return mix(1.0, s, uCloudShadowXform.w);
}

// Sun irradiance reaching a point (before shadow & N.L), includes atmospheric transmittance + clouds
vec3 sunIrradianceAt(vec3 wp) {
  return uSunColorTOA * transmittanceAt(wp.y, uSunDir) * cloudShadowAt(wp);
}

float D_GGX(float NoH, float a) {
  float a2 = a * a;
  float f = (NoH * a2 - NoH) * NoH + 1.0;
  return a2 / (PI * f * f + 1e-7);
}
float V_SmithGGXCorrelated(float NoV, float NoL, float a) {
  float a2 = a * a;
  float gv = NoL * sqrt(NoV * NoV * (1.0 - a2) + a2);
  float gl = NoV * sqrt(NoL * NoL * (1.0 - a2) + a2);
  return 0.5 / (gv + gl + 1e-5);
}
vec3 F_Schlick(vec3 f0, float VoH) {
  float f = pow(1.0 - VoH, 5.0);
  return f + f0 * (1.0 - f);
}

// Full shading of an opaque surface. Returns outgoing radiance.
// skyVis: sky visibility / ambient occlusion (0..1). bounceAlbedo: color of surrounding rock for canyon inter-reflection.
vec3 shadeSurface(vec3 albedo, vec3 N, vec3 V, vec3 wp, float roughness, float f0s, float shadow,
                  float skyVis, float ao, vec3 bounceAlbedo, float translucency) {
  vec3 L = uSunDir;
  float NoL = dot(N, L);
  vec3 Esun = sunIrradianceAt(wp) * shadow;
  float NoLc = max(NoL, 0.0);
  vec3 H = normalize(L + V);
  float NoV = max(dot(N, V), 1e-3);
  float NoH = max(dot(N, H), 0.0);
  float VoH = max(dot(V, H), 0.0);
  float a = roughness * roughness;
  vec3 f0 = vec3(f0s);
  vec3 F = F_Schlick(f0, VoH);
  vec3 spec = D_GGX(NoH, a) * V_SmithGGXCorrelated(NoV, NoLc, a) * F;
  vec3 diffuse = albedo * INV_PI * (1.0 - F * 0.5);
  vec3 direct = (diffuse + spec) * Esun * NoLc;
  // thin-surface translucency (foliage) – light through leaves
  direct += albedo * INV_PI * Esun * max(-NoL, 0.0) * translucency;

  // Ambient: sky (SH) attenuated by sky visibility; canyon walls replace the occluded sky.
  vec3 Esky = shIrradiance(N) * PI;
  vec3 groundBounce = skyIrrTexel(10);
  vec3 Ewall = bounceAlbedo * (groundBounce + Esky * 0.25);
  float up = N.y * 0.5 + 0.5;
  vec3 Eamb = Esky * skyVis + Ewall * (1.0 - skyVis) + bounceAlbedo * groundBounce * (1.0 - up) * 0.9;
  Eamb += skyIrrTexel(11); // night airglow / moonless floor
  vec3 ambient = albedo * INV_PI * Eamb * ao;
  // ambient specular: crude, sky luminance reflected at grazing angles on wet surfaces
  vec3 R = reflect(-V, N);
  vec3 envSpec = shIrradiance(R) * PI * F_Schlick(f0, NoV) * (1.0 - roughness) * skyVis * ao;
  vec3 flash = albedo * INV_PI * uLightning * vec3(5.0, 5.4, 6.5) * (0.35 + 0.65 * up) * skyVis;
  return direct + ambient + envSpec + flash;
}
`;

// Sky visibility of the terrain around a point (near level, packed in the normal texture's B channel).
// Objects use it so that, deep in a canyon, they receive the same warm wall bounce as the ground.
export const terrainVisGLSL = /* glsl */ `
uniform sampler2D uTerrainVis;
uniform vec4 uTerrainVisB; // x0, z0, size, enabled
float terrainSkyVis(vec3 wp) {
  if (uTerrainVisB.w < 0.5) return 1.0;
  vec2 uv = (wp.xz - uTerrainVisB.xy) / uTerrainVisB.z;
  if (any(lessThan(uv, vec2(0.0))) || any(greaterThan(uv, vec2(1.0)))) return 1.0;
  return texture(uTerrainVis, uv).b;
}
`;
